import type { AdminRole } from '@/lib/api-admin/types';
import type { Settlement } from '@/lib/api-admin/types';

export const SETTLEMENT_ROUTE_ROLES: ReadonlyArray<AdminRole> = [
  'MD_MANAGER',
  'OPS_ADMIN',
  'SETTLEMENT_AGENT',
  'SUPER_ADMIN',
  'AUDITOR',
];

export const SETTLEMENT_GENERATE_ROLES: ReadonlyArray<AdminRole> = [
  'MD_MANAGER',
  'OPS_ADMIN',
  'SUPER_ADMIN',
];

export const SETTLEMENT_APPROVE_1ST_ROLES: ReadonlyArray<AdminRole> = [
  'MD_MANAGER',
  'OPS_ADMIN',
  'SETTLEMENT_AGENT',
  'SUPER_ADMIN',
];

export const SETTLEMENT_APPROVE_2ND_ROLES: ReadonlyArray<AdminRole> = ['SUPER_ADMIN'];

export const SETTLEMENT_PAYOUT_ROLES: ReadonlyArray<AdminRole> = ['SUPER_ADMIN'];

export const RELEASE_WRITE_ROLES: ReadonlyArray<AdminRole> = [
  'MD_MANAGER',
  'SUPER_ADMIN',
];

export const has = (
  roles: ReadonlyArray<AdminRole>,
  allow: ReadonlyArray<AdminRole>,
): boolean => allow.some((r) => roles.includes(r));

export const canGenerateSettlement = (roles: ReadonlyArray<AdminRole>): boolean =>
  has(roles, SETTLEMENT_GENERATE_ROLES);

export const canApprove1st = (roles: ReadonlyArray<AdminRole>): boolean =>
  has(roles, SETTLEMENT_APPROVE_1ST_ROLES);

export const canApprove2nd = (roles: ReadonlyArray<AdminRole>): boolean =>
  has(roles, SETTLEMENT_APPROVE_2ND_ROLES);

export const canPayout = (roles: ReadonlyArray<AdminRole>): boolean =>
  has(roles, SETTLEMENT_PAYOUT_ROLES);

export const canWriteRelease = (roles: ReadonlyArray<AdminRole>): boolean =>
  has(roles, RELEASE_WRITE_ROLES);

export type ApprovalStage = '1ST' | '2ND' | 'NONE';

/**
 * Pick the next approval action for the current run status, factoring 2-man rule.
 * 1ST is available when status is COMPUTED or PENDING_APPROVAL.
 * 2ND is available when status is AGENT_APPROVED.
 * `currentAdminId` is checked against creator/1st-approver to enforce DEC-021 2-man rule.
 */
export const nextApprovalStage = (
  run: Pick<
    Settlement,
    'status' | 'triggerAdminId' | 'approvedByAgentId' | 'approvedBySuperAdminId'
  >,
  currentAdminId: string | null,
  roles: ReadonlyArray<AdminRole>,
): { stage: ApprovalStage; blockedBySelfRule: boolean } => {
  if (run.status === 'COMPUTED' || run.status === 'PENDING_APPROVAL') {
    if (!canApprove1st(roles)) return { stage: 'NONE', blockedBySelfRule: false };
    const blocked =
      currentAdminId != null &&
      run.triggerAdminId != null &&
      currentAdminId === run.triggerAdminId;
    return { stage: '1ST', blockedBySelfRule: blocked };
  }
  if (run.status === 'AGENT_APPROVED') {
    if (!canApprove2nd(roles)) return { stage: 'NONE', blockedBySelfRule: false };
    const blocked =
      currentAdminId != null &&
      ((run.triggerAdminId != null && currentAdminId === run.triggerAdminId) ||
        (run.approvedByAgentId != null && currentAdminId === run.approvedByAgentId));
    return { stage: '2ND', blockedBySelfRule: blocked };
  }
  return { stage: 'NONE', blockedBySelfRule: false };
};

export const canExecutePayout = (
  run: Pick<Settlement, 'status'>,
  roles: ReadonlyArray<AdminRole>,
): boolean => canPayout(roles) && run.status === 'SUPER_APPROVED';
